import { ACCESS_CONTROL_FORM_DEFAULT_VALUES } from "@/features/management-settings/forms/constants/access-control-form.constants";
import type { AccessControlFormSchemaValues } from "@/features/management-settings/forms/schemas/access-control-form.schema";
import { accessControlFormSchema } from "@/features/management-settings/forms/schemas/access-control-form.schema";
import type { CompanyListItem } from "@/models/company.model";
import { showAlertCallout } from "@/shared/alerts";
import { useCompanyRepository } from "@/shared/contexts/repository-context";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import {
  getFindAccessControlsQueryKey,
  useCreateAccessControl,
} from "@/spz-api/access-controls/access-controls";
import { getGetCompanyListQueryKey } from "@/spz-api/companies/companies";
import { useGetCompanyGroupListPaginated } from "@/spz-api/company-groups/company-groups";
import {
  type CreateAccessControlRequestDto,
  ObjectDtoType,
  type SubjectDto,
  SubjectDtoType,
} from "@/spz-api/model";
import { useGetRoleListPaginated } from "@/spz-api/roles/roles";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UseAccessControlFormProps {
  subjectId: SubjectDto["id"];
  subjectType: SubjectDtoType;
}

export const useAccessControlForm = ({
  subjectId,
  subjectType,
}: UseAccessControlFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyRepository = useCompanyRepository();

  const roleList = useGetRoleListPaginated({
    page: 1,
    pageSize: 500,
  });
  const { data: companyList = [] } = useQuery<CompanyListItem[]>({
    queryKey: getGetCompanyListQueryKey(),
    queryFn: () => companyRepository.getCompanies(),
  });
  const companyGroupList = useGetCompanyGroupListPaginated({
    page: 1,
    pageSize: 500,
  });

  const form = useForm<AccessControlFormSchemaValues>({
    resolver: zodResolver(accessControlFormSchema(t)),
    defaultValues: ACCESS_CONTROL_FORM_DEFAULT_VALUES,
    mode: "onSubmit",
  });

  const createAccessControlMutation = useCreateAccessControl({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("create_access_control_success"),
        });

        queryClient.invalidateQueries({
          queryKey: getFindAccessControlsQueryKey({}),
        });
        form.reset(ACCESS_CONTROL_FORM_DEFAULT_VALUES);
      },
      onError: (error: unknown) => {
        const { translationKey, displayTypes } =
          getTranslationKeyAndDisplayTypes(error);
        const message = t(translationKey);
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.ALERT)) {
          showAlertCallout({
            type: "warning",
            title: message,
          });

          return;
        }

        showAlertCallout({
          type: "critical",
          title: t("create_access_control_error"),
        });
      },
    },
  });

  const onSubmit = async (data: AccessControlFormSchemaValues) => {
    if (!data.company || !data.role) {
      showAlertCallout({
        type: "critical",
        title: t("create_access_control_error"),
      });

      return;
    }

    const isCompanyGroup = companyGroupList.data?.data.some(
      (item) => item.id === data.company,
    );

    const createAccessControlRequestDto: CreateAccessControlRequestDto = {
      subject: {
        id: subjectId,
        type: subjectType,
      },
      object: {
        id: data.company,
        type: isCompanyGroup
          ? ObjectDtoType.COMPANY_GROUP
          : ObjectDtoType.COMPANY,
      },
      roleId: data.role,
    };

    if (subjectType === SubjectDtoType.USER_GROUP) {
      createAccessControlMutation.mutate({
        data: createAccessControlRequestDto,
      });
    } else {
      createAccessControlMutation.mutate({
        data: createAccessControlRequestDto,
      });
    }
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: createAccessControlMutation.isPending,
    companies: {
      sections: [
        {
          title: t("company_group"),
          options: (companyGroupList.data?.data ?? []).map((item) => ({
            id: item.id,
            label: item.name,
            value: item.id,
            type: ObjectDtoType.COMPANY_GROUP,
          })),
        },
        {
          title: t("company"),
          options: companyList.map((item) => ({
            id: item.id,
            label: item.name,
            value: item.id,
            type: ObjectDtoType.COMPANY,
          })),
        },
      ],
    },
    roles: {
      options: (roleList.data?.data ?? []).map((item) => ({
        id: item.id,
        label: item.name,
        value: item.id,
      })),
    },
  };
};
